import { A, useLocation, useNavigate } from "@solidjs/router";
import { createSignal, onCleanup, onMount, Show, type ParentProps } from "solid-js";

import HelpOverlay from "./components/HelpOverlay";
import SpotlightSearch from "./components/SpotlightSearch";
import { auth, isPublicRoute, logout, refreshMe } from "./lib/auth";

function inField(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return (
    t instanceof HTMLInputElement ||
    t instanceof HTMLTextAreaElement ||
    t.isContentEditable
  );
}

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/.test(navigator.platform);

function readTheme(): "light" | "dark" {
  try {
    return localStorage.getItem("fs-theme") === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function applyTheme(theme: "light" | "dark") {
  const root = document.documentElement;
  if (theme === "light") root.setAttribute("data-theme", "light");
  else root.removeAttribute("data-theme");
  try {
    localStorage.setItem("fs-theme", theme);
  } catch {}
}

// One-time "this is built for desktop" nudge. Dismissed permanently per device.
const DESKTOP_HINT_KEY = "fs-desktop-hint-seen";

function desktopHintSeen(): boolean {
  try {
    return localStorage.getItem(DESKTOP_HINT_KEY) === "1";
  } catch {
    return true; // storage blocked: fail closed so we never nag.
  }
}

function markDesktopHintSeen() {
  try {
    localStorage.setItem(DESKTOP_HINT_KEY, "1");
  } catch {}
}

export default function App(props: ParentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = createSignal(false);
  const [spotlightOpen, setSpotlightOpen] = createSignal(false);
  const [theme, setTheme] = createSignal<"light" | "dark">(readTheme());
  const [desktopHint, setDesktopHint] = createSignal(false);

  const dismissDesktopHint = () => {
    markDesktopHintSeen();
    setDesktopHint(false);
  };

  const toggleTheme = () => {
    const next = theme() === "light" ? "dark" : "light";
    setTheme(next);
    applyTheme(next);
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // App nav (search, dashboard, firms, api, ...) only makes sense once you're
  // inside the app. On public routes (/login, /thank-you) the header is just
  // the brand + theme toggle.
  const showAppNav = () => !isPublicRoute(location.pathname);

  // Brand glyph + wordmark, shared by both link variants below.
  const brandMark = () => (
    <>
      <span class="glyph">&#9635;</span>
      <span class="brand-text">
        FORTUNE 500 <span class="slash">/</span>{" "}
        <span class="sub">business models</span>
      </span>
    </>
  );

  onMount(() => {
    // Desktop-first product: nudge phone visitors once, then never again.
    if (desktopHintSeen()) return;
    const small =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 720px)").matches;
    if (small) setDesktopHint(true);
  });

  onMount(() => {
    // Identity check + 401 gate. Public SPA routes (/login, /thank-you) render
    // regardless; everything else bounces to /login?next= when unauthenticated.
    refreshMe().then((state) => {
      if (state.status === "anon" && !isPublicRoute(location.pathname)) {
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${next}`, { replace: true });
      }
    });
  });

  onMount(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd+K / Ctrl+K opens spotlight from anywhere (even inside inputs)
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setHelpOpen(false);
        setSpotlightOpen(true);
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "Escape") {
        if (spotlightOpen()) {
          setSpotlightOpen(false);
          return;
        }
        if (helpOpen()) {
          setHelpOpen(false);
          return;
        }
        if (e.target instanceof HTMLElement) e.target.blur();
        return;
      }

      if (inField(e.target)) return;

      if (e.key === "?") {
        e.preventDefault();
        setHelpOpen(true);
      } else if (e.key === "g" && !helpOpen() && !spotlightOpen()) {
        e.preventDefault();
        navigate("/firms");
      } else if (e.key === "d" && !helpOpen() && !spotlightOpen()) {
        e.preventDefault();
        navigate("/dashboard");
      } else if (e.key === "s" && !helpOpen() && !spotlightOpen()) {
        e.preventDefault();
        navigate("/sectors");
      }
    };
    document.addEventListener("keydown", handler);
    onCleanup(() => document.removeEventListener("keydown", handler));
  });

  return (
    <div class="page">
      <header class="topbar">
        <Show
          when={showAppNav()}
          fallback={
            // Public routes (login, thank-you): the wordmark goes back to the
            // marketing landing page at "/", which the backend serves outside
            // the SPA, so this is a real navigation (plain <a>), not a router link.
            <a class="brand" href="/" title="Back to landing page">
              {brandMark()}
            </a>
          }
        >
          <A class="brand" href="/dashboard" title="Home (dashboard)">
            {brandMark()}
          </A>
        </Show>
        <nav class="links">
          <Show when={showAppNav()}>
          <button
            class="cmd-k-hint"
            type="button"
            onClick={() => setSpotlightOpen(true)}
            aria-label="Search firms"
            title="Search firms (Cmd+K)"
          >
            <span class="cmd-k-icon" aria-hidden>&#9906;</span>
            <span class="cmd-k-text">search</span>
            <span class="cmd-k-keys">
              <kbd>{IS_MAC ? "⌘" : "Ctrl"}</kbd>
              <kbd>K</kbd>
            </span>
          </button>
          <A href="/dashboard">dashboard</A>
          <A href="/firms">firms</A>
          <A href="/api">api</A>
          <Show when={auth().status === "authed" && (auth() as { status: "authed"; me: { is_admin: boolean } }).me.is_admin}>
            <A href="/admin" class="admin-link">admin</A>
          </Show>
          <Show when={auth().status === "authed"}>
            {(() => {
              const state = auth();
              if (state.status !== "authed") return null;
              return (
                <span class="topbar-identity">
                  <span class="topbar-email" title={state.me.email ?? ""}>
                    {state.me.email ?? "—"}
                  </span>
                  <button class="link-btn" type="button" onClick={doLogout} title="Sign out">
                    ⎋
                  </button>
                </span>
              );
            })()}
          </Show>
          <button
            class="link-btn"
            type="button"
            onClick={() => setHelpOpen(true)}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (press ?)"
          >
            ?
          </button>
          </Show>
          <button
            class="theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle light or dark theme"
            title="Toggle theme"
          >
            <span class="ti dark" aria-hidden="true">☽</span>
            <span class="sep" aria-hidden="true">·</span>
            <span class="ti light" aria-hidden="true">☀</span>
          </button>
        </nav>
      </header>
      <main class="content">{props.children}</main>
      <Show when={helpOpen()}>
        <HelpOverlay onClose={() => setHelpOpen(false)} />
      </Show>
      <Show when={spotlightOpen()}>
        <SpotlightSearch onClose={() => setSpotlightOpen(false)} />
      </Show>
      <Show when={desktopHint()}>
        <div class="dt-hint" role="dialog" aria-modal="true" aria-label="Best viewed on desktop">
          <div class="dt-hint-card">
            <div class="dt-hint-eyebrow">Built for desktop</div>
            <p class="dt-hint-body">
              This is a dense analyst workspace. It reads best on a wider screen.
              For the full view, open it on desktop.
            </p>
            <button class="dt-hint-btn" type="button" onClick={dismissDesktopHint}>
              Continue anyway
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
}
